import type { TaskCompletionInvalidationScope } from "@remi/enums/task/completion-invalidation-scope";
import type { EqualWithDiff, Expect } from "@remi/ts-utils";
import { assertNever, isOneOf } from "@remi/ts-utils";
import type { CompletionPhase } from "@remi/types/socket/message";

export type CompletionTrackerPhase = "pending" | "delayed" | "committed" | "evaluated";

// Ranked, not a flat enum: transitions compare ranks, so a duplicate publish (an Inngest step replay
// after a lost ack) or an out-of-order one is mechanically a no-op.
export const PHASE_RANK = {
  pending: 0,
  delayed: 1,
  committed: 2,
  evaluated: 3,
} as const satisfies Record<CompletionTrackerPhase, number>;

export type CompletionTerminalKind = "failed" | "raceLost" | "aborted";

// Sibling completions on one project settle within milliseconds of each other under the bid-scoped
// concurrency key, so running the set once per window costs a fraction of the queries.
export const INVALIDATION_COALESCE_WINDOW_MS = 250;

export const shouldCoalesceInvalidation = (previousAt: number | undefined, now: number): boolean =>
  previousAt !== undefined && now - previousAt < INVALIDATION_COALESCE_WINDOW_MS;

export type InvalidationScopes = readonly TaskCompletionInvalidationScope[] | undefined;

export const getScopesToRun = (lastRan: InvalidationScopes, requested: InvalidationScopes): InvalidationScopes => {
  if (requested === undefined) {
    return undefined;
  }
  if (lastRan === undefined) {
    return [];
  }
  return requested.filter((scope) => !lastRan.includes(scope));
};

export const mergeInvalidationScopes = (left: InvalidationScopes, right: InvalidationScopes): InvalidationScopes =>
  left === undefined || right === undefined ? undefined : [...new Set([...left, ...right])];

// Strip query/hash before comparing: both sides come from `router.asPath`, and a shallow push that only
// changes the query (e.g. closing the drawer) must not read as having left the page.
const pathnameOf = (path: string): string => path.split(/[?#]/, 1)[0] ?? path;

// Yanking a user off an unrelated page 20-40s after they asked is worse than not navigating at all.
export const shouldRunNavigationIntent = (registeredPathname: string, currentPathname: string): boolean =>
  pathnameOf(registeredPathname) === pathnameOf(currentPathname);

// Below `committed` the server has not confirmed anything, while the optimistic write already shows the
// task as completed — that gap is what the pending affordance exists to show.
export const isCompletionPending = (entries: readonly { phase: CompletionTrackerPhase }[]): boolean =>
  entries.some((entry) => PHASE_RANK[entry.phase] < PHASE_RANK.committed);

// Attempt-tracker vs completion-tracker precedence: CompletionTracker's phases are real server-confirmed
// CAS results, while AttemptTracker's pending state precedes any commit — so when a separate completion
// path races an in-flight attempt on the same task, completion pending wins.
export const shouldSuppressAttemptPending = (isTaskCompletionPending: boolean): boolean => isTaskCompletionPending;

type CompletionMessageClass = { kind: "ordinary" } | { kind: CompletionPhase };

const WIRE_COMPLETION_PHASES = [
  "committed",
  "evaluated",
  "failed",
  "raceLost",
  "aborted",
] as const satisfies readonly CompletionPhase[];

// `satisfies` alone is a subset assertion, so a sixth CompletionPhase member would pass it and then be
// degraded to `ordinary` by the runtime branch below, stranding the entry until its TTL. Exact equality
// catches OUR additions at build time; the runtime degradation is for a newer SERVER's.
type _WireCompletionPhasesExact = Expect<EqualWithDiff<(typeof WIRE_COMPLETION_PHASES)[number], CompletionPhase>>;

export interface CompletionChannelMessage {
  completionNonce?: string;
  completionPhase?: string;
}

export const classifyCompletionMessage = (message: CompletionChannelMessage): CompletionMessageClass => {
  if (message.completionNonce === undefined || message.completionPhase === undefined) {
    return { kind: "ordinary" };
  }
  // An unrecognized phase degrades to ordinary rather than failing closed: the wire field is a loose
  // string so a future phase from a newer server does not strand this client on a wrong terminal.
  if (!isOneOf(message.completionPhase, WIRE_COMPLETION_PHASES)) {
    return { kind: "ordinary" };
  }
  return { kind: message.completionPhase };
};

export const COMPLETION_UNCONFIRMED = "We couldn't confirm that completion. Refreshing this task.";

// `failed` after `committed` means the task IS durably complete and a follow-up step is not, so the
// copy must not read as a failed completion and the optimistic write must not be reverted.
export const COMPLETION_TAIL_FAILED = "Task completed. A follow-up step failed — repair pending.";

export const COMPLETION_ABORTED = "That task changed before it completed — refreshing.";

// `aborted` after `committed` is a superseded tail: the commit is durable, so do not revert, and
// the copy must not read as a failed completion.
export const COMPLETION_SUPERSEDED = "Task completed. Follow-up work was replaced — refreshing.";

interface CompletionTerminalDisposition {
  revert: boolean;
  toast: { title: string; variant: "error" | "warning" } | null;
}

// `raceLost` is deliberately silent: another durable run owns the completion, so the user has
// nothing to act on.
export const getTerminalDisposition = (
  phase: CompletionTrackerPhase,
  kind: CompletionTerminalKind,
): CompletionTerminalDisposition => {
  const observedCommitted = PHASE_RANK[phase] >= PHASE_RANK.committed;

  switch (kind) {
    case "failed":
      return observedCommitted
        ? { revert: false, toast: { title: COMPLETION_TAIL_FAILED, variant: "warning" } }
        : { revert: true, toast: { title: COMPLETION_UNCONFIRMED, variant: "error" } };
    case "aborted":
      return observedCommitted
        ? { revert: false, toast: { title: COMPLETION_SUPERSEDED, variant: "warning" } }
        : { revert: true, toast: { title: COMPLETION_ABORTED, variant: "error" } };
    case "raceLost":
      return { revert: true, toast: null };
    default:
      return assertNever(kind, `unhandled completion terminal kind: ${String(kind)}`);
  }
};

export type LifecycleTrackerKind = "task-lifecycle" | "ticket-lifecycle" | "project-lifecycle";

export const LIFECYCLE_WRITE_FAILED = "The task change could not be applied. Refreshing this task.";

const LIFECYCLE_ACTOR_UNAVAILABLE = "You can no longer make this task change. Refreshing this task.";

export const LIFECYCLE_TAIL_FAILED = "Task updated. Reassigning it failed — repair pending.";

const LIFECYCLE_UNCONFIRMED = "We couldn't confirm that task change. Refreshing this task.";

export const TICKET_LIFECYCLE_WRITE_FAILED = "The ticket change could not be applied. Refreshing this ticket.";

export const TICKET_LIFECYCLE_ACTOR_UNAVAILABLE = "You can no longer make this ticket change. Refreshing this ticket.";

const TICKET_LIFECYCLE_TAIL_FAILED = "Ticket updated. Reassigning it failed — repair pending.";

const TICKET_LIFECYCLE_UNCONFIRMED = "We couldn't confirm that ticket change. Refreshing this ticket.";

export const PROJECT_LIFECYCLE_WRITE_FAILED =
  "The project status change could not be applied. Refreshing this project.";

const PROJECT_LIFECYCLE_ACTOR_UNAVAILABLE = "You can no longer change this project's status. Refreshing this project.";

const PROJECT_LIFECYCLE_TAIL_FAILED = "Project status updated. Reassigning its work failed — repair pending.";

const PROJECT_LIFECYCLE_UNCONFIRMED = "We couldn't confirm that project status change. Refreshing this project.";

interface LifecycleCopy {
  writeFailed: string;
  actorUnavailable: string;
  tailFailed: string;
  unconfirmed: string;
}

export const LIFECYCLE_COPY = {
  "task-lifecycle": {
    writeFailed: LIFECYCLE_WRITE_FAILED,
    actorUnavailable: LIFECYCLE_ACTOR_UNAVAILABLE,
    tailFailed: LIFECYCLE_TAIL_FAILED,
    unconfirmed: LIFECYCLE_UNCONFIRMED,
  },
  "ticket-lifecycle": {
    writeFailed: TICKET_LIFECYCLE_WRITE_FAILED,
    actorUnavailable: TICKET_LIFECYCLE_ACTOR_UNAVAILABLE,
    tailFailed: TICKET_LIFECYCLE_TAIL_FAILED,
    unconfirmed: TICKET_LIFECYCLE_UNCONFIRMED,
  },
  "project-lifecycle": {
    writeFailed: PROJECT_LIFECYCLE_WRITE_FAILED,
    actorUnavailable: PROJECT_LIFECYCLE_ACTOR_UNAVAILABLE,
    tailFailed: PROJECT_LIFECYCLE_TAIL_FAILED,
    unconfirmed: PROJECT_LIFECYCLE_UNCONFIRMED,
  },
} as const satisfies Record<LifecycleTrackerKind, LifecycleCopy>;

// Log event names are a contract with saved Axiom queries, so each kind keeps its own names.
export const LIFECYCLE_LOG_EVENTS = {
  "task-lifecycle": {
    evaluated: "task.lifecycle.client.evaluated",
    terminal: "task.lifecycle.client.terminal",
    settleTimedOut: "task.lifecycle.client.settle-timed-out",
    expired: "task.lifecycle.client.expired",
  },
  "ticket-lifecycle": {
    evaluated: "ticket.lifecycle.client.evaluated",
    terminal: "ticket.lifecycle.client.terminal",
    settleTimedOut: "ticket.lifecycle.client.settle-timed-out",
    expired: "ticket.lifecycle.client.expired",
  },
  "project-lifecycle": {
    evaluated: "project.lifecycle.client.evaluated",
    terminal: "project.lifecycle.client.terminal",
    settleTimedOut: "project.lifecycle.client.settle-timed-out",
    expired: "project.lifecycle.client.expired",
  },
} as const satisfies Record<
  LifecycleTrackerKind,
  { evaluated: string; terminal: string; settleTimedOut: string; expired: string }
>;

// The workflow publishes `aborted` only when step 1 refused the actor, and `failed` for every other failure.
export const getLifecycleTerminalDisposition = (
  trackerKind: LifecycleTrackerKind,
  phase: CompletionTrackerPhase,
  kind: CompletionTerminalKind,
): CompletionTerminalDisposition => {
  const copy = LIFECYCLE_COPY[trackerKind];
  const observedCommitted = PHASE_RANK[phase] >= PHASE_RANK.committed;
  switch (kind) {
    case "failed":
      return observedCommitted
        ? { revert: false, toast: { title: copy.tailFailed, variant: "warning" } }
        : { revert: true, toast: { title: copy.writeFailed, variant: "error" } };
    case "aborted":
      return { revert: !observedCommitted, toast: { title: copy.actorUnavailable, variant: "error" } };
    case "raceLost":
      return { revert: true, toast: null };
    default:
      return assertNever(kind, `unhandled lifecycle terminal kind: ${String(kind)}`);
  }
};
